import { defineMessages, useIntl } from "react-intl";
import { gql } from "@apollo/client";
import { useMutation} from "@apollo/client/react";
import { IconCheck, IconMailShare, IconX } from "@tabler/icons-react";
import { AnyPeriod, classNames, toGQLPeriod, useAppConfig } from "@ct-react/core";
import { MailInput } from "@shared/graphql";
import { actions, form } from "../i18n/sharable-defs";
import { useForm } from "../hooks/form";
import { DataProps } from "../tools/components";
import { validators } from "../tools/form";
import Button from "./common/button";
import "./contact-form.scss";

export const contact_form = defineMessages({
  contact_type: { id: "contact-form.contact-type", defaultMessage: "Comment préferez vous être contacté ?" },
  accept_conditions: { id: "contact-form.accept-conditions", defaultMessage: "J'accepte que Verbier Rentals traite mes données personnelles afin de répondre à ma demande. Elles ne seront pas partagées avec des tiers. J'ai lu la politique de confidentialité." },
});

type Props = {
  className?: string;
  related?: { articleId: string, period?: AnyPeriod; };
};

const SEND_MAIL_TO_AGENCY_GQL_MUT = gql`
  mutation MailAgency($agencyId: ID!, $request: MailRequestInput!) {
    mailRelatedContact(merchantId: $agencyId, request: $request)
  }
`;

const ContactForm = ({ className, related }: Props) => {

  const intl = useIntl();
  const { options: { agencyId } } = useAppConfig();

  const { register, formState, handleSubmit } = useForm<MailInput>();
  const [ sendMail ] = useMutation(SEND_MAIL_TO_AGENCY_GQL_MUT);

  const submit = async (data: Partial<MailInput>) =>
    await sendMail({
      variables: {
        agencyId,
        request: {
          ...data,
          ...(!!related) && {
            article: {
              ...related,
              ...(!!related.period) && { period: toGQLPeriod(related.period) }
            }
          }
        }
      }
    }).then(() => void 0);

  const wrapperClasses = classNames("contact-form", className);

  return (
    <form className={wrapperClasses}
          onSubmit={handleSubmit(submit, { resetValues: true, resetStateSubmitTimer: 5000 })}>
      <div className="f-group">
        <input type="text"
               className={classNames({ dirty: "contact.firstName" in formState.dirtyFields })}
               {...register("contact.firstName", { required: intl.formatMessage(form.emptyError) })}
               placeholder={intl.formatMessage(form.firstName)}
               autoComplete="given-name" />
        <p className="f-error">
          {!!formState.dirtyFields["contact.firstName"] && formState.dirtyFields["contact.firstName"].error}
        </p>
      </div>
      <div className="f-group">
        <input type="text"
               className={classNames({ dirty: "contact.lastName" in formState.dirtyFields })}
               {...register("contact.lastName", { required: intl.formatMessage(form.emptyError) })}
               placeholder={intl.formatMessage(form.lastName)}
               autoComplete="family-name" />
        <p className="f-error">
          {!!formState.dirtyFields["contact.lastName"] && formState.dirtyFields["contact.lastName"].error}
        </p>
      </div>
      <div className="f-group">
        <input type="email"
               className={classNames({ dirty: "contact.email" in formState.dirtyFields })}
               {...register("contact.email", {
                 required: intl.formatMessage(form.emptyError),
                 validators: { ...validators.emailFormat(intl.formatMessage(form.emailError)) }
               })}
               placeholder={intl.formatMessage(form.email)}
               autoComplete="email" />
        <p className="f-error">
          {!!formState.dirtyFields["contact.email"] && formState.dirtyFields["contact.email"].error}
        </p>
      </div>
      <div className="f-group">
        <input type="tel"
               className={classNames({ dirty: "contact.phone" in formState.dirtyFields })}
               {...register("contact.phone", {
                 required: intl.formatMessage(form.emptyError),
                 validators: { ...validators.phoneFormat(intl.formatMessage(form.phoneError)) }
               })}
               placeholder={intl.formatMessage(form.phone)}
               autoComplete="tel" />
        <p className="f-error">
          {!!formState.dirtyFields["contact.phone"] && formState.dirtyFields["contact.phone"].error}
        </p>
      </div>
      <div className="f-group">
        <textarea className={classNames({ dirty: "body" in formState.dirtyFields })}
                  {...register("body", {
                    required: intl.formatMessage(form.emptyError),
                    validators: { ...validators.minLength(20, intl.formatMessage(form.minError, { min: 20 }) )}
                  })}
                  placeholder={intl.formatMessage(form.msgPlaceholder)}
                  rows={5} />
        <p className="f-error">
          {!!formState.dirtyFields["body"] && formState.dirtyFields["body"].error}
        </p>
      </div>
      <div className="f-group preference">
        <span>{intl.formatMessage(contact_form.contact_type)}</span>
        <label>
          <input type="radio" value="Email" name="preference" defaultChecked={true}/>
          {intl.formatMessage(form.email)}
        </label>
        <label>
          <input type="radio" value="Phone" name="preference"/>
          {intl.formatMessage(form.phone)}
        </label>
      </div>
      <div className="f-group acceptance">
        <label>
          <input type="checkbox" name="policy-acceptance"/>
          {intl.formatMessage(contact_form.accept_conditions)}
        </label>
      </div>
      <div className="submitter">
        {(formState.submit.finished) &&
          <span className="callback">
            {(formState.submit.succeeded)
              ? <>{intl.formatMessage(form.sent)}<IconCheck className="s-icon" /></>
              : <>{intl.formatMessage(form.failed)}<IconX className="f-icon" /></>
            }
          </span>
        }
        <Button type="submit"
                className="primary with-icon"
                processing={formState.submit.inProcess}
                disabled={!formState.isValid}>
          <IconMailShare />
          {intl.formatMessage(actions.send)}
        </Button>
      </div>
    </form>);

};

export default ContactForm;
